/**
 * useAccountRevenue
 *
 * Aggregates revenue across all sites with Stripe (or Foxy) connected, and
 * computes a 7d-vs-prior-7d delta from the existing `getPropertyRevenue`
 * response. The backend doesn't have a `?compare=prior` query param, so we
 * pull the last 14 days of attribution rows and bucket them client-side.
 *
 * Drives:
 *   - REVENUE_ANOMALY hero variant (gated on |delta| >= 20%)
 *   - A future "revenue this week" surface in the PlanCard or RealtimeRibbon
 *
 * Soft-fail per property — a missing revenue config or a 500 on one site
 * doesn't break aggregation across the rest.
 */

import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getPropertyRevenue, getRevenueConfig } from '@/lib/api/api'

interface UseAccountRevenueInput {
  properties: Array<{
    propertyId: string
    name: string
    domain: string
    isDemo?: boolean
    archived?: boolean
  }>
}

interface UseAccountRevenueResult {
  /** True if at least one property has Stripe or Foxy connected. */
  isConfigured: boolean
  /** Aggregate revenue (dollars) for the last 7 days. */
  revenueThisWeek: number
  /** Aggregate revenue (dollars) for the prior 7 days (days 8-14 ago). */
  revenueLastWeek: number
  /** Rounded percent change. Null if last week was zero (can't divide). */
  deltaPct: number | null
  /** Site that drove the biggest share of this week's revenue. */
  topContributor: { propertyId: string; name: string; sharePct: number } | null
  isLoading: boolean
}

/**
 * Bucket attribution rows into this-week vs last-week sums (in dollars).
 * Uses the `granularity` / `created_at` / `date` field on each row — same
 * fallback chain useAttributionData uses.
 */
function bucketRevenue(rows: any[]): { thisWeek: number; lastWeek: number } {
  const now = Date.now()
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000
  const thisWeekStart = now - SEVEN_DAYS_MS
  const lastWeekStart = now - 2 * SEVEN_DAYS_MS

  let thisWeek = 0
  let lastWeek = 0
  for (const row of rows) {
    const dateStr = row?.granularity || row?.created_at || row?.date
    if (!dateStr) continue
    const ts = new Date(dateStr).getTime()
    if (Number.isNaN(ts)) continue
    const cents = Number(row?.total_revenue ?? row?.amount_cents ?? row?.total_amount_cents ?? 0)
    if (!cents) continue
    const dollars = cents / 100
    if (ts >= thisWeekStart) thisWeek += dollars
    else if (ts >= lastWeekStart) lastWeek += dollars
  }
  return { thisWeek, lastWeek }
}

export function useAccountRevenue({
  properties,
}: UseAccountRevenueInput): UseAccountRevenueResult {
  const { token } = useAuth()

  const targets = useMemo(
    () =>
      properties
        .filter((p) => !p.isDemo && !p.archived)
        .map((p) => ({ propertyId: p.propertyId, name: p.name, domain: p.domain })),
    [properties]
  )

  // ── Revenue config (Stripe / Foxy connection check) ─────────────────────
  const configResults = useQueries({
    queries: targets.map(({ propertyId }) => ({
      queryKey: ['revenue-config', propertyId],
      queryFn: async () => {
        if (!token) return null
        try {
          return await getRevenueConfig(propertyId, token)
        } catch {
          return null
        }
      },
      enabled: !!token,
      staleTime: 10 * 60 * 1000,
      retry: false,
    })),
  })

  // ── Last-14-day revenue rolls (we'll bucket into 7d windows client-side) ─
  const revenueResults = useQueries({
    queries: targets.map(({ propertyId }, i) => {
      const cfg = configResults[i]?.data as any
      const isConfigured =
        cfg?.revenueSetup?.stripe?.configured === true ||
        cfg?.revenueSetup?.foxy?.configured === true
      return {
        queryKey: ['account-revenue-14d', propertyId],
        queryFn: async () => {
          if (!token) return null
          try {
            // Pull 30d worth so we have enough room to bucket 14d cleanly even
            // if the backend's granularity rounds to days.
            return await getPropertyRevenue('LAST_30_DAYS', propertyId, token)
          } catch {
            return null
          }
        },
        enabled: !!token && !!isConfigured,
        staleTime: 5 * 60 * 1000,
        retry: false,
      }
    }),
  })

  return useMemo<UseAccountRevenueResult>(() => {
    let isConfigured = false
    let revenueThisWeek = 0
    let revenueLastWeek = 0
    let anyLoading = false
    const contributorByProperty: Record<string, { name: string; thisWeek: number }> = {}

    targets.forEach((t, i) => {
      const cfgQ = configResults[i]
      const revQ = revenueResults[i]
      if (cfgQ?.isLoading || revQ?.isLoading) anyLoading = true

      const cfg = cfgQ?.data as any
      const propertyConfigured =
        cfg?.revenueSetup?.stripe?.configured === true ||
        cfg?.revenueSetup?.foxy?.configured === true
      if (propertyConfigured) isConfigured = true

      const rev = revQ?.data as any
      const rows = Array.isArray(rev?.revenueAttribution) ? rev.revenueAttribution : []
      if (rows.length === 0) return

      const { thisWeek, lastWeek } = bucketRevenue(rows)
      revenueThisWeek += thisWeek
      revenueLastWeek += lastWeek
      contributorByProperty[t.propertyId] = { name: t.name, thisWeek }
    })

    const deltaPct =
      revenueLastWeek > 0
        ? Math.round(((revenueThisWeek - revenueLastWeek) / revenueLastWeek) * 100)
        : null

    // Largest single contributor to this week's revenue (for hero copy).
    let topContributor: UseAccountRevenueResult['topContributor'] = null
    if (revenueThisWeek > 0) {
      const ranked = Object.entries(contributorByProperty)
        .filter(([, v]) => v.thisWeek > 0)
        .sort((a, b) => b[1].thisWeek - a[1].thisWeek)
      const top = ranked[0]
      if (top) {
        topContributor = {
          propertyId: top[0],
          name: top[1].name,
          sharePct: Math.round((top[1].thisWeek / revenueThisWeek) * 100),
        }
      }
    }

    return {
      isConfigured,
      revenueThisWeek,
      revenueLastWeek,
      deltaPct,
      topContributor,
      isLoading: anyLoading,
    }
  }, [targets, configResults, revenueResults])
}
